/**
 * @Description: 请求缓存工具
 * 用于缓存指定的网络请求，减少重复请求
 */

/**
 * 缓存配置项
 */
export interface CacheConfig {
  url: string; // 需要缓存的请求URL（支持通配符 * ）
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'; // 请求方法，默认不限制
  duration?: number; // 缓存时长（毫秒），默认5分钟
}

/**
 * 默认缓存配置列表
 * 只需要在这里添加需要缓存的接口配置即可
 */
export const DEFAULT_CACHE_CONFIGS: CacheConfig[] = [
  {
    url: '/email-ticket/my-statics',
    method: 'GET',
    duration: 5 * 60 * 1000, // 5分钟
  },
  {
    url: '/config/email-ticket',
    method: 'GET',
    duration: 5 * 60 * 1000, // 5分钟
  },
  // 可以继续添加更多缓存配置
  // {
  //   url: '/api/dictionary/*', // 支持通配符
  //   method: 'GET',
  //   duration: 10 * 60 * 1000, // 10分钟
  // },
];

/**
 * 缓存项结构
 */
interface CacheItem<T = any> {
  data: T; // 缓存的数据
  timestamp: number; // 缓存时间戳
  expireTime: number; // 过期时间（毫秒）
}

/**
 * 请求缓存类
 */
class RequestCache {
  private cache = new Map<string, CacheItem>();
  private configs: CacheConfig[];
  private readonly defaultDuration = 5 * 60 * 1000; // 默认5分钟

  constructor(configs: CacheConfig[] = []) {
    this.configs = configs;
  }

  /**
   * 生成缓存key
   */
  private getCacheKey(url: string, method: string, params?: any): string {
    const paramsStr = params ? JSON.stringify(params) : '';
    return `${method}:${url}:${paramsStr}`;
  }

  /**
   * URL匹配（支持通配符 * ）
   */
  private matchUrl(pattern: string, url: string): boolean {
    if (pattern.includes('*')) {
      const regex = new RegExp(`^${pattern.replace(/\*/g, '.*')}$`);
      return regex.test(url);
    }
    return pattern === url;
  }

  /**
   * 查找匹配的缓存配置
   */
  private findConfig(url: string, method: string): CacheConfig | null {
    return (
      this.configs.find(
        (cfg) => this.matchUrl(cfg.url, url) && (!cfg.method || cfg.method === method.toUpperCase()),
      ) || null
    );
  }

  /**
   * 获取缓存
   */
  get<T = any>(url: string, method: string, params?: any): T | null {
    const config = this.findConfig(url, method);
    if (!config) return null;

    const key = this.getCacheKey(url, method, params);
    const item = this.cache.get(key);
    if (!item) return null;

    // 检查是否过期
    if (Date.now() - item.timestamp > item.expireTime) {
      this.cache.delete(key);
      return null;
    }

    return item.data as T;
  }

  /**
   * 设置缓存
   */
  set(url: string, method: string, data: any, params?: any): void {
    const config = this.findConfig(url, method);
    if (!config) return;

    const key = this.getCacheKey(url, method, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expireTime: config.duration || this.defaultDuration,
    });
  }

  /**
   * 清除指定缓存
   */
  clear(url: string, method: string, params?: any): void {
    const key = this.getCacheKey(url, method, params);
    this.cache.delete(key);
  }

  /**
   * 清除所有缓存
   */
  clearAll(): void {
    this.cache.clear();
  }
}

// 创建单例实例，自动加载默认配置
export const requestCache = new RequestCache(DEFAULT_CACHE_CONFIGS);
// 实例后门
window._requestCache = requestCache;
